import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, GUEST_TOKEN_KEY } from "@/lib/api";
import SmoothScroll from "@/components/SmoothScroll";
import GuestGate from "@/components/guest/GuestGate";
import GuestNav from "@/components/guest/GuestNav";
import Hero from "@/components/guest/Hero";
import SectionBlock from "@/components/guest/SectionBlock";
import GuestChecklist from "@/components/guest/GuestChecklist";
import GuestCalendar from "@/components/guest/GuestCalendar";
import ReportIssue from "@/components/guest/ReportIssue";

function Loader() {
  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <motion.p
        className="font-display text-2xl text-foreground/60"
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ repeat: Infinity, duration: 1.6 }}
      >
        Preparing your stay…
      </motion.p>
    </div>
  );
}

function Welcome({ message }) {
  if (!message) return null;
  return (
    <section className="px-6 sm:px-10 lg:px-16 py-24 md:py-32 border-b border-border" data-testid="welcome">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="overline text-accent">About This Guide</span>
          <p className="font-display text-3xl md:text-5xl leading-[1.15] tracking-tight text-foreground mt-6">
            {message}
          </p>
        </motion.div>
      </div>
    </section>
  );
}

function Footer({ property }) {
  return (
    <footer className="bg-foreground text-primary-foreground px-6 sm:px-10 lg:px-16 py-20" data-testid="footer">
      <div className="max-w-6xl mx-auto">
        <p className="subhead text-accent">Enjoy your stay</p>
        <h3 className="font-display text-5xl md:text-7xl tracking-tight mt-4 leading-[0.9]">
          {property?.name}
        </h3>
        <p className="text-primary-foreground/70 mt-6 max-w-md">{property?.address}</p>
        <div className="mt-16 pt-8 border-t border-primary-foreground/20 flex flex-wrap gap-4 justify-between text-xs text-primary-foreground/50">
          <span>Digital Guidebook · La Quinta, CA</span>
          <span>Questions? Message your host anytime.</span>
        </div>
      </div>
    </footer>
  );
}

export default function Guidebook() {
  const [unlocked, setUnlocked] = useState(!!localStorage.getItem(GUEST_TOKEN_KEY));
  const [data, setData] = useState(null);
  const [checklistItems, setChecklistItems] = useState(null);
  const [calendarEntries, setCalendarEntries] = useState(null);
  const [status, setStatus] = useState("idle");

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const res = await api.get("/guidebook");
      setData(res.data);
      try {
        const cl = await api.get("/departure-checklist");
        setChecklistItems(cl.data.items || []);
      } catch {
        setChecklistItems(null); // category doesn't include the checklist
      }
      try {
        const cal = await api.get("/calendar");
        setCalendarEntries(cal.data.entries || []);
      } catch {
        setCalendarEntries(null); // category doesn't include the calendar
      }
      setStatus("done");
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(GUEST_TOKEN_KEY);
        setUnlocked(false);
      }
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    if (unlocked) load();
  }, [unlocked, load]);

  const exit = () => {
    localStorage.removeItem(GUEST_TOKEN_KEY);
    setUnlocked(false);
    setData(null);
    setChecklistItems(null);
    setCalendarEntries(null);
  };

  if (!unlocked) return <GuestGate onUnlock={() => setUnlocked(true)} />;
  if (status === "loading" || status === "idle" || !data) return <Loader />;

  const { property, sections } = data;

  return (
    <SmoothScroll>
      <div className="relative grain bg-background min-h-screen">
        <GuestNav sections={sections} hasCalendar={calendarEntries !== null} onExit={exit} />
        <Hero property={property} />
        <Welcome message={property?.welcome_message} />
        <div>
          {sections.map((s, i) => {
            // Check-Out: owners/guests/manager/cleaner get the full interactive Departure
            // Checklist instead of the short renter list
            if (s.key === "check-out" && checklistItems?.length) {
              const intro = s.content.split(/Before you go/)[0].trim();
              return (
                <SectionBlock key={s.key} section={{ ...s, content: intro }} index={i}>
                  <GuestChecklist items={checklistItems} />
                </SectionBlock>
              );
            }
            return <SectionBlock key={s.key} section={s} index={i} />;
          })}
        </div>
        {calendarEntries !== null && <GuestCalendar entries={calendarEntries} />}
        <ReportIssue />
        <Footer property={property} />
      </div>
    </SmoothScroll>
  );
}
